import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Annotation, AnnotationBanner } from "@/components/Annotation";
import { TrendingUp, Target, Users, Layers, ArrowUpRight } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, Cell
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatINR = (val) => {
  if (val >= 10000000) return `${(val / 10000000).toFixed(1)}Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(1)}L`;
  if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
  return Math.round(val);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs shadow-xl">
      <p className="text-slate-300 font-medium mb-1">Month {label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-mono">
          {p.name}: INR {Number(p.value).toLocaleString()}
        </p>
      ))}
    </div>
  );
};

export default function LTVAnalysis() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [segment, setSegment] = useState("repeat");

  useEffect(() => {
    axios.get(`${API}/ltv/analysis`).then((res) => {
      setData(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 skeleton-shimmer rounded" />
        <div className="grid grid-cols-3 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-28 skeleton-shimmer rounded" />)}
        </div>
        <div className="h-[300px] skeleton-shimmer rounded" />
      </div>
    );
  }

  const allBench = data.benchmarks || {};
  const repeatBench = data.repeat_benchmarks || {};
  const activatedBench = data.activated_benchmarks || {};
  const segments = data.segments || {};
  const expansionRatios = data.expansion_ratios || [];
  const avgExpansion = data.avg_expansion_ratio || 0;

  const activeCurve = segment === "all" ? data.curve : segment === "repeat" ? data.repeat_curve : data.activated_curve;
  const activeBench = segment === "all" ? allBench : segment === "repeat" ? repeatBench : activatedBench;
  const activeLabel = segment === "all" ? "All Customers" : segment === "repeat" ? "Repeat Customers (2+ Orders)" : "Activated (2+ Categories)";
  const activeCount = segment === "all" ? segments.all_customers : segment === "repeat" ? segments.repeat_customers : segments.activated_customers;

  const growthPct = activeBench["3m"] > 0 ? (((activeBench["12m"] - activeBench["3m"]) / activeBench["3m"]) * 100).toFixed(0) : 0;

  // Combine curves for comparison chart
  const comparisonData = (data.curve || []).map((p, i) => ({
    month: p.month,
    all: p.avg_ltv,
    repeat: data.repeat_curve?.[i]?.avg_ltv || null,
    activated: data.activated_curve?.[i]?.avg_ltv || null,
  }));

  // Top expansion cohorts
  const topExpansion = [...expansionRatios].sort((a, b) => b.expansion_ratio - a.expansion_ratio).slice(0, 12);

  return (
    <div className="space-y-6" data-testid="ltv-analysis-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-space text-2xl sm:text-3xl font-bold text-slate-100 tracking-tight">
            LTV Analysis
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Segmented lifetime value &middot; Revenue per customer over time
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-emerald-400 border-emerald-800 bg-emerald-950/30 text-xs font-mono" data-testid="avg-expansion-badge">
            Avg Expansion: {avgExpansion}%
          </Badge>
        </div>
      </div>

      {/* Segment Comparison Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          {
            label: "All Customers", count: segments.all_customers, bench: allBench,
            color: "text-slate-400", border: "border-slate-700", key: "all"
          },
          {
            label: "Repeat Customers", count: segments.repeat_customers, bench: repeatBench,
            color: "text-emerald-400", border: "border-emerald-800", key: "repeat",
            note: "2+ orders"
          },
          {
            label: "Activated Customers", count: segments.activated_customers, bench: activatedBench,
            color: "text-blue-400", border: "border-blue-800", key: "activated",
            note: "2+ categories"
          },
        ].map((seg) => {
          const growth = seg.bench["3m"] > 0 ? (((seg.bench["12m"] - seg.bench["3m"]) / seg.bench["3m"]) * 100).toFixed(0) : 0;
          return (
            <Card
              key={seg.key}
              className={`kpi-card border-slate-800 bg-slate-900/50 backdrop-blur-xl cursor-pointer transition-all duration-300 ${segment === seg.key ? `ring-1 ${seg.border}` : "hover:border-slate-700"}`}
              onClick={() => setSegment(seg.key)}
              data-testid={`ltv-segment-${seg.key}`}
            >
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-slate-400 font-medium uppercase tracking-wider">{seg.label}</span>
                  {seg.note && <span className="text-[9px] text-slate-600 bg-slate-800 px-1.5 py-0.5 rounded">{seg.note}</span>}
                </div>
                <p className="font-mono text-xs text-slate-500 mb-3">{seg.count?.toLocaleString()} customers</p>
                <div className="flex items-baseline gap-3">
                  <div>
                    <p className="text-[10px] text-slate-500">12M LTV</p>
                    <p className={`font-space text-xl font-bold tracking-tight ${seg.color}`}>
                      INR {formatINR(seg.bench["12m"] || 0)}
                    </p>
                  </div>
                  <div className="ml-auto text-right">
                    <p className="text-[10px] text-slate-500">3M→12M</p>
                    <p className={`font-mono text-sm font-semibold ${Number(growth) > 10 ? "text-emerald-400" : "text-slate-400"}`}>
                      +{growth}%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <AnnotationBanner title="Why Segmented LTV Matters">
        <p>The "All Customers" LTV appears flat because our product range is exclusively durable baby gear — strollers, cribs, walkers, and high chairs are inherently one-time purchases with no same-SKU repeat. A customer buying a crib will never buy another crib. <strong className="text-amber-300">Repeat here means buying a different product category entirely</strong>, which is a high-intent, high-trust signal.</p>
        <p className="mt-1.5">The true indicator is the <strong className="text-amber-300">Repeat Customer LTV</strong>: these {segments.repeat_customers?.toLocaleString()} customers show +{growthPct}% LTV expansion over 12 months. This is further understated because: (a) customers who repurchase via Amazon/Flipkart (lower prices during sales) are invisible to us, (b) new-account creation for first-time discount coupons fragments customer identity, and (c) our no-COD, no-returns website policy means many first-time buyers test on marketplaces first. The {avgExpansion}% expansion ratio represents only the customers we can positively identify on our website.</p>
      </AnnotationBanner>

      {/* Active Segment LTV Benchmarks */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: "3-Month LTV", value: activeBench["3m"], period: "3M", color: "text-blue-400" },
          { label: "6-Month LTV", value: activeBench["6m"], period: "6M", color: "text-emerald-400" },
          { label: "12-Month LTV", value: activeBench["12m"], period: "12M", color: "text-amber-400" },
        ].map((b) => (
          <div key={b.label} className="glass-card rounded-md p-4" data-testid={`ltv-bench-${b.period.toLowerCase()}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] text-slate-500 uppercase tracking-wider">{b.label}</span>
              <Badge variant="outline" className={`${b.color} text-[10px] border-slate-700`}>{b.period}</Badge>
            </div>
            <p className={`font-space text-xl font-bold tracking-tight ${b.color}`}>INR {formatINR(b.value || 0)}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">{activeLabel} &middot; {activeCount?.toLocaleString()}</p>
          </div>
        ))}
      </div>

      {/* Comparison Chart: All vs Repeat vs Activated */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="ltv-comparison-chart">
        <CardHeader className="pb-2">
          <CardTitle className="font-space text-base font-semibold text-slate-200 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-emerald-400" />
            LTV Curve Comparison — All vs Repeat vs Activated
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={320}>
            <AreaChart data={comparisonData}>
              <defs>
                <linearGradient id="repeatGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="activGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis dataKey="month" tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false}
                label={{ value: "Months Since Acquisition", position: "bottom", fill: "#64748b", fontSize: 11 }} />
              <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false}
                tickFormatter={(v) => `${formatINR(v)}`} />
              <RechartsTooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="repeat" stroke="#10b981" fill="url(#repeatGrad)" strokeWidth={2.5} name="Repeat Customers" />
              <Area type="monotone" dataKey="activated" stroke="#3b82f6" fill="url(#activGrad)" strokeWidth={2} strokeDasharray="6 3" name="Activated (2+ Cat)" />
              <Area type="monotone" dataKey="all" stroke="#64748b" fill="none" strokeWidth={1.5} strokeDasharray="4 4" name="All Customers" />
            </AreaChart>
          </ResponsiveContainer>
          <div className="flex items-center gap-6 mt-3 text-xs text-slate-400">
            <div className="flex items-center gap-2">
              <div className="w-4 h-0.5 bg-emerald-500 rounded" />
              <span>Repeat Customers ({segments.repeat_customers?.toLocaleString()})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-0.5 bg-blue-500 rounded" style={{ borderTop: "2px dashed #3b82f6", height: 0 }} />
              <span>Activated ({segments.activated_customers?.toLocaleString()})</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-0.5 bg-slate-500 rounded" style={{ borderTop: "2px dashed #64748b", height: 0 }} />
              <span>All ({segments.all_customers?.toLocaleString()})</span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Annotation>
        The widening gap between Repeat (green) and All (grey) curves is the investor signal. Repeat customers progressively buy higher-value items as their child grows — starting with a walker or feeding accessories, then moving to strollers, cribs, and furniture. This maps directly to the baby development lifecycle (0–6M, 6–12M, 1–2Y). Note: this curve only captures website-traceable repurchases; the actual cross-platform LTV including marketplace purchases would show an even steeper trajectory.
      </Annotation>

      {/* Revenue Expansion Ratio */}
      {topExpansion.length > 0 && (
        <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="expansion-ratio-chart">
          <CardHeader className="pb-2">
            <CardTitle className="font-space text-base font-semibold text-slate-200 flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4 text-amber-400" />
              Revenue Expansion Ratio by Cohort
              <span className="text-[10px] text-slate-500 font-normal ml-2 bg-slate-800/60 px-2 py-0.5 rounded">
                Repeat Revenue / Initial Revenue &middot; Mature Cohorts Only
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={topExpansion} margin={{ left: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                <XAxis dataKey="cohort" tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} axisLine={false} interval={0} angle={-45} textAnchor="end" height={50} />
                <YAxis tick={{ fill: '#64748b', fontSize: 11 }} tickLine={false} axisLine={false}
                  tickFormatter={(v) => `${v}%`} />
                <RechartsTooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0].payload;
                    return (
                      <div className="bg-slate-900 border border-slate-700 rounded-md px-3 py-2 text-xs shadow-xl">
                        <p className="font-semibold text-slate-200 mb-1">{d.cohort} ({d.cohort_size} customers)</p>
                        <p className="text-slate-400">Initial Revenue: <span className="text-slate-200 font-mono">INR {formatINR(d.initial_revenue)}</span></p>
                        <p className="text-slate-400">Repeat Revenue: <span className="text-emerald-400 font-mono">INR {formatINR(d.repeat_revenue)}</span></p>
                        <p className="text-slate-400">Expansion Ratio: <span className="text-amber-400 font-mono">{d.expansion_ratio}%</span></p>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="expansion_ratio" name="Expansion %" radius={[4, 4, 0, 0]}>
                  {topExpansion.map((e, i) => (
                    <Cell key={i} fill={e.expansion_ratio >= 25 ? "#10b981" : e.expansion_ratio >= 15 ? "#3b82f6" : "#64748b"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Cohort LTV Table */}
      <Card className="border-slate-800 bg-slate-900/50 backdrop-blur-xl" data-testid="cohort-ltv-table">
        <CardHeader className="pb-3">
          <CardTitle className="font-space text-base font-semibold text-slate-200 flex items-center gap-2">
            <Target className="w-4 h-4 text-blue-400" />
            Cohort LTV Comparison (All vs Repeat)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="cohort-ltv-data-table">
              <thead>
                <tr className="border-b border-slate-800">
                  <th className="text-left py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">Cohort</th>
                  <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">Size</th>
                  <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">Repeat</th>
                  <th className="text-right py-2 px-3 text-xs text-slate-500 uppercase tracking-wider font-semibold">All 12M</th>
                  <th className="text-right py-2 px-3 text-xs text-emerald-600 uppercase tracking-wider font-semibold">Repeat 12M</th>
                  <th className="text-right py-2 px-3 text-xs text-slate-400 uppercase tracking-wider font-semibold">Repeat Growth</th>
                </tr>
              </thead>
              <tbody>
                {(data.cohort_ltv || []).map((c) => {
                  const rGrowth = c.repeat_ltv_3m > 0 ? (((c.repeat_ltv_12m - c.repeat_ltv_3m) / c.repeat_ltv_3m) * 100).toFixed(0) : "—";
                  return (
                    <tr key={c.cohort} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                      <td className="py-2.5 px-3 font-mono text-slate-300">{c.cohort}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-slate-400">{c.size?.toLocaleString()}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-slate-400">{c.repeat_count?.toLocaleString() || "—"}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-slate-500">{formatINR(c.ltv_12m)}</td>
                      <td className="py-2.5 px-3 text-right font-mono text-emerald-400 font-medium">{formatINR(c.repeat_ltv_12m)}</td>
                      <td className="py-2.5 px-3 text-right font-mono">
                        <span className={rGrowth !== "—" && Number(rGrowth) > 0 ? "text-emerald-400" : "text-slate-500"}>
                          {rGrowth !== "—" ? `+${rGrowth}%` : "—"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
